import { Component, DestroyRef, OnInit, OnDestroy, inject, ChangeDetectorRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { ExtractedSumWarningService } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import { CrossTabLayoutChangeNotifierService } from '../../../services/mbrs/cross-tab-layout-change-notifier.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { Router } from '@angular/router';
import { ReloadPromptComponent } from '../../common/reload-prompt/reload-prompt.component';
import { ReloadBannerComponent } from '../../common/reload-banner/reload-banner.component';
import { stripAllValidatorsRecursively } from '../../../utils/form-validation.util';
import {
  CROSS_SHEET_VALIDATION_DEBOUNCE_MS,
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
// Shared rules consumed by both this component and the pure validator
// (role14SoplByNatureValidator). Centralising the schema-file picker and the
// scope-of-filing / accounting-standard literals here keeps the dual-path
// parity harness honest — see role14-sopl-by-nature.rules.ts.
import {
  pickRole14SchemaFile,
  ROLE14_COMPARATIVE_INFO_FIELD,
  ROLE14_COMPARATIVE_INFO_PRESENT_VALUES,
  ROLE14_CONSOLIDATED_FIELD,
  ROLE14_CONSOLIDATED_VALUE,
} from '../../../services/mbrs/validators/role14-sopl-by-nature.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
  TOAST_DURATION_SHORT_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors, enableAndRevealErrors } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface FieldControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    calculation?: string;
    level?: number;
    note?: string;
    decimalPlaces?: number;
  };
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: FieldControl[];
}

@Component({
  selector: 'app-role14-320000-statement-of-profit-or-loss-by-nature',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    Tooltip,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    ReloadPromptComponent,
    ReloadBannerComponent,
    PlaceholderPipe,
    FieldLinkTargetDirective
  ],
  providers: [MessageService],
  templateUrl: './role14-320000-statement-of-profit-or-loss-by-nature.component.html',
  styleUrls: ['./role14-320000-statement-of-profit-or-loss-by-nature.component.css']
})
// Developer Note (TODO): NOT migrated to the shared MappingStatusService (Mapping-Status
// "Edited vs Validated" refactor, 2026-05). This sheet is not in active support/use, AND its
// `originalState` is not populated by filing-form-container (no `populate…FromMbrsData` /
// `loadFromAPI(SHEET_KEYS.statementOfProfitOrLossByNature, …)`), so deriving "Edited" from the
// last-submission baseline would mark every row Edited. When this sheet is revived:
//   1. Add a `populateStatementOfProfitOrLossByNatureFromMbrsData` path in filing-form-container
//      (so getOriginalSheetData returns real data), and add its tuple to populateSnapshotFromMbrsData.
//   2. Then apply the standard migration: delegate getRowValidationStatus to MappingStatusService,
//      re-source validateAgainstApiData from getSnapshotValues, delete validationBaseline + editedRows.
// Until then it keeps its legacy per-sheet validationBaseline/editedRows logic.
export class Role14320000StatementOfProfitOrLossByNatureComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  private layoutChangeNotifier = inject(CrossTabLayoutChangeNotifierService);
  private cdr = inject(ChangeDetectorRef);
  private envService = inject(EnvironmentService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();
  private readonly sheetKey = SHEET_KEYS.statementOfProfitOrLossByNature;
  private calculatedTotalKeys: string[] = [];

  statementOfProfitOrLossByNatureForm!: FormGroup;
  schema: FormSchema | null = null;
  isLoading = true;
  isConsolidated = false;
  hasPreviousYear = false;
  private initialIsMFRS = false;

  showReloadPrompt = signal<boolean>(false);
  reloadPromptDismissed = signal<boolean>(false);
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Profit or Loss (By Nature) (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfProfitOrLossByNature
    );
  }
  private editingRows = new Map<string, { [key: string]: number | null }>(); // Track rows in edit mode with original values
  private editedRows = new Set<string>(); // Track rows that have been edited and saved (in-memory only, clears on page refresh)

  // Validation baseline for warnings (persisted to localStorage)
  private validationBaseline: Record<string, any> | null = null;
  private validationErrors = new Map<string, string[]>();

  /**
   * Get validation baseline key with conversionId scope
   */
  private get VALIDATION_BASELINE_KEY(): string {
    return `mbrs-validation-baseline-role14-${this.stateService.getConversionId()}`;
  }

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get period header for current year
   * e.g., "For the year ended 31 December 2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getPeriodHeader('current');
  }

  /**
   * Get period header for previous year
   * e.g., "For the year ended 31 December 2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getPeriodHeader('previous');
  }

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '14',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    this.checkConsolidatedStatus();
    this.loadSchema();
    this.loadValidationBaseline(); // Load validation baseline from localStorage
    this.setupLayoutReactivity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load validation baseline from localStorage
   */
  private loadValidationBaseline(): void {
    try {
      const stored = localStorage.getItem(this.VALIDATION_BASELINE_KEY);
      if (stored) {
        this.validationBaseline = JSON.parse(stored);
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('💾 Validation baseline loaded from localStorage');
      }
    } catch (error) {
      console.error('Error loading validation baseline from localStorage:', error);
      this.validationBaseline = null;
    }
  }

  /**
   * Save validation baseline to localStorage
   */
  private saveValidationBaseline(): void {
    try {
      if (this.validationBaseline) {
        localStorage.setItem(
          this.VALIDATION_BASELINE_KEY,
          JSON.stringify(this.validationBaseline)
        );
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('💾 Validation baseline saved to localStorage');
      }
    } catch (error) {
      console.error('Error saving validation baseline to localStorage:', error);
    }
  }

  /**
   * Clear validation baseline from localStorage (for cleanup)
   */
  clearValidationBaseline(): void {
    try {
      localStorage.removeItem(this.VALIDATION_BASELINE_KEY);
      this.validationBaseline = null;
      this.validationErrors.clear();
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('🗑️ Validation baseline cleared');
    } catch (error) {
      console.error('Error clearing validation baseline:', error);
    }
  }

  /**
   * Check consolidation status and comparative information from Role-2
   */
  private checkConsolidatedStatus(): void {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    this.isConsolidated =
      scopeOfFilingData?.[ROLE14_CONSOLIDATED_FIELD] === ROLE14_CONSOLIDATED_VALUE;

    const comparativeInfo = scopeOfFilingData?.[ROLE14_COMPARATIVE_INFO_FIELD];
    this.hasPreviousYear =
      typeof comparativeInfo === 'string' &&
      ROLE14_COMPARATIVE_INFO_PRESENT_VALUES.includes(comparativeInfo);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Consolidated status:', this.isConsolidated);
    // console.log('Has previous year:', this.hasPreviousYear);
  }

  // Why: pure predicate, no mutation of isConsolidated / hasPreviousYear.
  // Under the reload-prompt model the component's layout MUST stay frozen
  // until the user reloads — mutating the flags here would break the
  // schema/data invariant the cached FormGroup relies on. Compare-only,
  // return boolean, let the notifier surface the prompt.
  private detectLayoutFlagsChanged(): boolean {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    const freshConsolidated =
      scopeOfFilingData?.[ROLE14_CONSOLIDATED_FIELD] === ROLE14_CONSOLIDATED_VALUE;
    const comparativeInfo = scopeOfFilingData?.[ROLE14_COMPARATIVE_INFO_FIELD];
    const freshPreviousYear =
      typeof comparativeInfo === 'string' &&
      ROLE14_COMPARATIVE_INFO_PRESENT_VALUES.includes(comparativeInfo);
    const freshIsMFRS = this.crossRefService.isMFRS();

    return (
      freshConsolidated !== this.isConsolidated ||
      freshPreviousYear !== this.hasPreviousYear ||
      freshIsMFRS !== this.initialIsMFRS
    );
  }

  private router = inject(Router);

  private setupLayoutReactivity(): void {
    this.layoutChangeNotifier
      .watchSubmissionCommitted({
        sheetKey: this.sheetKey,
        slicesOfInterest: [SHEET_KEYS.filingPrerequisites, SHEET_KEYS.filingInfo, SHEET_KEYS.scopeOfFiling],
        detectChanged: () => this.detectLayoutFlagsChanged(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.reloadPromptDismissed()) {
          this.showReloadPrompt.set(true);
        }
      });
  }

  onReload(): void {
    window.location.reload();
  }

  onDismiss(): void {
    this.showReloadPrompt.set(false);
    this.reloadPromptDismissed.set(true);
    this.disableAllValidations();
  }

  onReturnToFiles(): void {
    this.showReloadPrompt.set(false);
    this.router.navigate(['/mbrs/xbrl-files']);
  }

  // Why: one-way door. Once the user dismisses the reload prompt the
  // in-memory form schema is known-stale vs. upstream scope; persisting
  // any further validation result would be misleading. We strip every
  // validator from every control (and the root FormGroup) so no spurious
  // errors accumulate before the user reloads. There is deliberately no
  // re-enable path — a fresh ngOnInit (via reload) is the only recovery.
  private disableAllValidations(): void {
    stripAllValidatorsRecursively(this.statementOfProfitOrLossByNatureForm);
  }

  /**
   * Load form schema from JSON
   * Dynamically loads MFRS or MPERS schema based on accounting standard from Role-0
   */
  private async loadSchema(): Promise<void> {
    this.isLoading = true;

    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role14');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Determine which schema to load based on accounting standard. The
    // shared `pickRole14SchemaFile` helper drives both this fetch and the
    // pure validator's static import — single source of truth for the
    // MFRS-exact / MPERS-fallback selection rule (mirrors role 13).
    const isMFRS = this.crossRefService.isMFRS();
    this.initialIsMFRS = isMFRS;
    // Pass the RAW accounting standard (not an isMFRS-collapsed value) so the
    // shared picker sees the real literal — matches role 13's pattern and
    // stays correct if role 14 ever moves to the MFRS-default `pickSchema`.
    const accountingStandard =
      this.crossRefService.getCrossReferenceData().accountingStandard;
    const schemaFile = pickRole14SchemaFile(accountingStandard);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`Loading schema: ${schemaFile} (MFRS: ${isMFRS})`);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    fetchSheetSchema<FormSchema>(
        this.loadTraceHandle,
        `assets/forms/${schemaFile}`,
      )
      .then((data: FormSchema) => {
        this.schema = data;
        this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('Schema loaded:', this.schema);
        
        this.buildForm();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
        this.setupEditPipeline();

        // Reveal the sheet's already-computed errors now that the form exists
        // and has been populated. Deterministic replacement for the filing-form
        // container's setTimeout-driven markAllAsTouched, which silently no-ops
        // when a sheet's async init has not yet assigned its FormGroup — see
        // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
        revealValidationErrors(this.statementOfProfitOrLossByNatureForm, {
          isComputed: computedControlPredicate(this.schema?.controls),
        });
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        this.setupStatePersistence();
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.statementOfProfitOrLossByNature, false);
        }, 600);
      })
      .catch(error => {
        console.error('Error loading schema:', error);
        // Classified rather than reported as a bare schema failure: this one
        // `.catch` also covers the cross-sheet preload, buildForm, the edit
        // pipeline and loadSavedState, so "Failed to load form schema" named
        // the wrong step for most of what can land here.
        this.loadTraceHandle.fail(classifySheetInitError(error), error);
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      });
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema) return;

    const formControls: any = {};

    this.schema.controls.forEach(control => {
      // Skip headers
      if (control.ui.control === 'header') return;

      const validators: ValidatorFn[] = [];

      if (control.ui.dualColumn) {
        // Use null for non-calculated fields, 0 for calculated/total fields
        // This ensures unmapped API data fields remain as NULL (not converted to 0)
        const defaultValue = control.ui.calculated ? 0 : null;

        // Scenario 1: Consolidated + Previous Year = 4 columns
        if (this.isConsolidated && this.hasPreviousYear) {
          formControls[control.qname + 'GroupCurrent'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'GroupPrevious'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'CompanyPrevious'] = new FormControl(defaultValue, validators);
        }
        // Scenario 2: Consolidated only = 2 columns (Group, Company)
        else if (this.isConsolidated && !this.hasPreviousYear) {
          formControls[control.qname + 'GroupCurrent'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(defaultValue, validators);
        }
        // Scenario 3: Previous Year only = 2 columns (Current, Previous)
        else if (!this.isConsolidated && this.hasPreviousYear) {
          formControls[control.qname + 'Current'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'Previous'] = new FormControl(defaultValue, validators);
        }
        // Scenario 4: No consolidation, no previous year = 1 column (Current)
        else {
          formControls[control.qname + 'Current'] = new FormControl(defaultValue, validators);
        }
      }
    });

    this.statementOfProfitOrLossByNatureForm = this.fb.group(formControls);

    // Disable calculated fields to prevent manual editing
    this.disableCalculatedFields();
  }

  /**
   * Disable calculated fields programmatically
   * Called after form creation to prevent "changed after checked" warnings
   */
  private disableCalculatedFields(): void {
    if (!this.schema) return;

    this.schema.controls.forEach(control => {
      if (this.isCalculatedField(control)) {
        // Disable all column variants for this calculated field
        if (this.isConsolidated && this.hasPreviousYear) {
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'GroupCurrent')?.disable({ emitEvent: false });
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'GroupPrevious')?.disable({ emitEvent: false });
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'CompanyCurrent')?.disable({ emitEvent: false });
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'CompanyPrevious')?.disable({ emitEvent: false });
        } else if (this.isConsolidated && !this.hasPreviousYear) {
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'GroupCurrent')?.disable({ emitEvent: false });
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'CompanyCurrent')?.disable({ emitEvent: false });
        } else if (!this.isConsolidated && this.hasPreviousYear) {
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'Current')?.disable({ emitEvent: false });
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'Previous')?.disable({ emitEvent: false });
        } else {
          this.statementOfProfitOrLossByNatureForm.get(control.qname + 'Current')?.disable({ emitEvent: false });
        }
      }
    });
  }

  /**
   * Check if control is a section header
   */
  isSectionHeader(control: FieldControl): boolean {
    return control.ui.control === 'header';
  }

  /**
   * Get header CSS class based on level
   */
  getHeaderClass(level?: number): string {
    if (level === 1) return 'section-header-major';
    if (level === 2) return 'section-header-minor';
    return 'section-header-minor';
  }

  /**
   * Check if field is calculated
   */
  isCalculatedField(control: FieldControl): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Calculate all totals and derived fields
   */
  private calculateTotals(): void {
    if (!this.schema) return;

    // Determine which column suffixes to use
    const suffixes: string[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious');
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'CompanyCurrent');
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('Current', 'Previous');
    } else {
      suffixes.push('Current');
    }

    const calculatedTotals = this.calculationService.calculateTotals_320000(
      this.statementOfProfitOrLossByNatureForm.getRawValue(),
      suffixes
    );

    Object.keys(calculatedTotals).forEach((controlName) => {
      this.statementOfProfitOrLossByNatureForm
        .get(controlName)
        ?.setValue(calculatedTotals[controlName], { emitEvent: false });
    });

    this.calculatedTotalKeys = Object.keys(calculatedTotals);
    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfProfitOrLossByNature,
      Object.keys(calculatedTotals)
    );
  }

  /**
   * Validate calculated totals against baseline data
   * Only validates calculated/readonly fields (Totals)
   */
  /**
   * Same-sheet validation — safe to run live during an in-edit valueChanges cycle.
   * Clears and rebuilds the single error map (role14 has no cross-sheet errors),
   * then triggers a single change detection to prevent mid-validation flicker.
   * Called live (from the pipeline) and from saveEditMode / cancelEditMode.
   */
  private validateSameSheet(): void {
    this.validationErrors.clear();
    this.validateAgainstApiData();
    // Single change detection after ALL validations complete
    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /**
   * Run all validation methods in sequence.
   * role14 is same-sheet only — delegates to validateSameSheet.
   * Kept for cross-sheet listener calls in setupStatePersistence.
   */
  private runAllValidations(): void {
    this.validateSameSheet();
  }

  /**
   * Validate calculated totals against baseline data.
   * Only manages API mismatch errors (those containing "does not match the extracted sum").
   * Preserves all other error types (cross-sheet, equality, mandatory, etc.)
   * set by other validation methods.
   * It does NOT call cdr.detectChanges() — the caller is responsible for triggering CD
   * after all validation methods have run.
   */
  private validateAgainstApiData(): void {
    if (!this.schema) return;

    // Build the visible suffix -> column-label list matching this sheet's
    // four layout scenarios. role14 uses WORD suffixes (qname + 'GroupCurrent',
    // etc.), not bracket suffixes, and has NO isVisibleColumn render gate in
    // getRowErrorDescription — so the label strings here are display-only and
    // must reproduce the old method's `suffix.replace(...)` output verbatim
    // ('Group Current' / 'Group Previous' / 'Company Current' /
    // 'Company Previous' / 'Current' / 'Previous').
    const columns: { key: string; label: string }[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      columns.push(
        { key: 'GroupCurrent',    label: 'Group Current' },
        { key: 'GroupPrevious',   label: 'Group Previous' },
        { key: 'CompanyCurrent',  label: 'Company Current' },
        { key: 'CompanyPrevious', label: 'Company Previous' },
      );
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      columns.push(
        { key: 'GroupCurrent',   label: 'Group Current' },
        { key: 'CompanyCurrent', label: 'Company Current' },
      );
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      columns.push(
        { key: 'Current',  label: 'Current' },
        { key: 'Previous', label: 'Previous' },
      );
    } else {
      columns.push({ key: 'Current', label: 'Current' });
    }

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.statementOfProfitOrLossByNature,
      controls: this.schema.controls,
      columns,
      getControlName: (qname, key) => qname + key,
      getCurrentValue: (name) => this.statementOfProfitOrLossByNatureForm.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema.controls, warnings);
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.stateService.markSheetInitializing(SHEET_KEYS.statementOfProfitOrLossByNature);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.statementOfProfitOrLossByNature);
    if (savedData && Object.keys(savedData).length > 0) {
      this.statementOfProfitOrLossByNatureForm.patchValue(savedData, { emitEvent: false });
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Loaded saved state:', savedData);

      // Set validation baseline if not already set (one-time setup)
      if (!this.validationBaseline || Object.keys(this.validationBaseline).length === 0) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('🎯 Setting validation baseline from saved data');
        this.validationBaseline = { ...savedData };
        this.saveValidationBaseline();
      }

      // Recalculate totals after loading
      setTimeout(() => {
        this.calculateTotals();
        this.runAllValidations();
      }, 100);
    } else {
      // Report sheet-level status even when no saved data
      setTimeout(() => {
      }, 100);
    }
  }

  /**
   * Wire the live (in-edit) feedback cycle: calc → validateSameSheet.
   * No persist — commit is deferred to Tick (saveEditMode). No evaluateStatus
   * — role14 has none. This is the structural seam that makes "no commit until
   * Tick" a pipeline property rather than a flag gate.
   */
  private setupEditPipeline(): void {
    buildEditModeLivePipeline({
      source$: this.statementOfProfitOrLossByNatureForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      calculate: () => this.calculateTotals(),
      validateSameSheet: () => this.validateSameSheet(),
      // no evaluateStatus — role14 has none
    });
  }

  /**
   * Setup state persistence (auto-save) — cross-sheet subs only.
   * The form.valueChanges persist sub has been folded into setupEditPipeline().
   */
  private setupStatePersistence(): void {
    // ----------------------------------
    // Cross-sheet local re-validation watch (golden pattern, role-19).
    //
    // Role-14 reads scopeOfFiling @ L177 (consolidation toggle controls
    // which column scenario applies) and filingPrerequisites (currency,
    // taxonomy variant). Same-tab SPA navigation back to this sheet should
    // trigger re-validation when either upstream slice has changed.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfProfitOrLossByNature)),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
          r2: state.scopeOfFiling,
        })),
        distinctUntilChanged(),
        debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.runAllValidations();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab safety net — sibling-tab edits to filingPrerequisites or
    // scopeOfFiling must trigger re-validation here too.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfProfitOrLossByNature)),
        filter(() => !!this.schema && !!this.statementOfProfitOrLossByNatureForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey, changedSheets }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role14-statementOfProfitOrLossByNature',
          //   sourceSheetKey: sheetKey,
          // });
        }

        // Own-slice change -> repaint this sheet's fields. Other-slice change ->
        // fall through to the cross-sheet revalidation tail only, as before.
        if (changedSheets.includes(SHEET_KEYS.statementOfProfitOrLossByNature))
          this.adoptRemoteState();

        this.runAllValidations();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption for role14 (SoPL by nature). Rows currently open in
   * edit mode are skipped — the user's in-flight work wins until they tick
   * (local overwrites and broadcasts) or press X (the row lands on the
   * adopted value; see `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `calculateTotals()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.statementOfProfitOrLossByNatureForm,
      this.stateService.getSheetData(SHEET_KEYS.statementOfProfitOrLossByNature),
      openControlNames
    );

    this.calculateTotals();
    this.validateSameSheet();
  }

  /**
   * Auto-fill form with demo data
   */
  autoFillDemo(): void {
    if (!this.statementOfProfitOrLossByNatureForm) return;

    const demoData: any = {};

    // Helper function to set values for all column scenarios
    const setDemoValues = (
      qname: string,
      groupCurrentVal: number,
      groupPreviousVal: number,
      companyCurrentVal: number,
      companyPreviousVal: number
    ) => {
      if (this.isConsolidated && this.hasPreviousYear) {
        demoData[qname + 'GroupCurrent'] = groupCurrentVal;
        demoData[qname + 'GroupPrevious'] = groupPreviousVal;
        demoData[qname + 'CompanyCurrent'] = companyCurrentVal;
        demoData[qname + 'CompanyPrevious'] = companyPreviousVal;
      } else if (this.isConsolidated && !this.hasPreviousYear) {
        demoData[qname + 'GroupCurrent'] = groupCurrentVal;
        demoData[qname + 'CompanyCurrent'] = companyCurrentVal;
      } else if (!this.isConsolidated && this.hasPreviousYear) {
        demoData[qname + 'Current'] = companyCurrentVal;
        demoData[qname + 'Previous'] = companyPreviousVal;
      } else {
        demoData[qname + 'Current'] = companyCurrentVal;
      }
    };

    // Revenue and Other Income
    setDemoValues('ssmt:RevenueByNature', 50000000, 45000000, 48000000, 43000000);
    setDemoValues('ssmt:OtherIncomeByNature', 500000, 450000, 450000, 400000);

    // Expenses by Nature
    setDemoValues('ssmt:ChangesInInventoriesOfFinishedGoodsAndWorkInProgressByNature', -500000, -400000, -480000, -380000);
    setDemoValues('ssmt:RawMaterialsAndConsumablesUsedByNature', -18000000, -16000000, -17500000, -15500000);
    setDemoValues('ssmt:EmployeeBenefitsExpenseByNature', -12000000, -10800000, -11650000, -10450000);
    setDemoValues('ssmt:DepreciationAndAmortisationExpenseByNature', -4900000, -4430000, -4770000, -4300000);
    setDemoValues('ssmt:ImpairmentLossRecognisedInProfitOrLoss', -150000, -120000, -143000, -113000);
    setDemoValues('ssmt:TransportationExpense', -350000, -280000, -330000, -260000);
    setDemoValues('ssmt:AdvertisingAndMarketingExpense', -600000, -500000, -580000, -480000);
    setDemoValues('ssmt:RentalExpenseByNature', -500000, -450000, -480000, -430000);
    setDemoValues('ssmt:UtilitiesExpenseByNature', -200000, -180000, -190000, -170000);
    setDemoValues('ssmt:ProfessionalFeesExpense', -800000, -700000, -770000, -670000);
    setDemoValues('ssmt:InsuranceExpenseByNature', -150000, -130000, -145000, -125000);
    setDemoValues('ssmt:ForeignExchangeLossByNature', -80000, -70000, -77000, -67000);
    setDemoValues('ssmt:OtherExpensesByNature', -1270000, -1340000, -1248000, -1310000);

    // Finance
    setDemoValues('ssmt:FinanceCostsByNature', -800000, -750000, -780000, -730000);
    setDemoValues('ssmt:FinanceIncomeByNature', 100000, 90000, 95000, 85000);

    // Share of Associates/JVs (consolidated only)
    if (this.isConsolidated) {
      setDemoValues('ssmt:ShareOfProfitOfAssociatesByNature', 200000, 180000, 0, 0);
      setDemoValues('ssmt:ShareOfProfitOfJointVenturesByNature', 150000, 120000, 0, 0);
    } else {
      setDemoValues('ssmt:ShareOfProfitOfAssociatesByNature', 0, 0, 0, 0);
      setDemoValues('ssmt:ShareOfProfitOfJointVenturesByNature', 0, 0, 0, 0);
    }

    // Income Tax Expense
    setDemoValues('ssmt:IncomeTaxExpenseByNature', -2500000, -2200000, -2400000, -2100000);

    // Attribution
    if (this.isConsolidated && this.hasPreviousYear) {
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNatureGroupCurrent'] = 7650000;
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNatureGroupPrevious'] = 6790000;
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNatureCompanyCurrent'] = 7087000;
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNatureCompanyPrevious'] = 6255000;

      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNatureGroupCurrent'] = 200000;
      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNatureGroupPrevious'] = 150000;
      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNatureCompanyCurrent'] = 0;
      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNatureCompanyPrevious'] = 0;
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNatureGroupCurrent'] = 7650000;
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNatureCompanyCurrent'] = 7087000;

      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNatureGroupCurrent'] = 200000;
      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNatureCompanyCurrent'] = 0;
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNatureCurrent'] = 7087000;
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNaturePrevious'] = 6255000;

      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNatureCurrent'] = 0;
      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNaturePrevious'] = 0;
    } else {
      demoData['ssmt:ProfitAttributableToOwnersOfParentByNatureCurrent'] = 7087000;
      demoData['ssmt:ProfitAttributableToNonControllingInterestsByNatureCurrent'] = 0;
    }

    // Earnings per share (sen)
    setDemoValues('ssmt:BasicEarningsPerShareByNature', 15.30, 13.58, 14.17, 12.51);
    setDemoValues('ssmt:DilutedEarningsPerShareByNature', 15.10, 13.40, 14.00, 12.35);

    this.statementOfProfitOrLossByNatureForm.patchValue(demoData);

    // Trigger calculations
    setTimeout(() => this.calculateTotals(), 100);

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: this.isConsolidated && this.hasPreviousYear
        ? 'Form filled with Group/Company and Current/Previous Year demo data'
        : this.isConsolidated
        ? 'Form filled with Group and Company demo data (Current Year only)'
        : this.hasPreviousYear
        ? 'Form filled with Current and Previous Year demo data'
        : 'Form filled with Current Year demo data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    this.statementOfProfitOrLossByNatureForm.reset();
    this.stateService.clearSheetData(SHEET_KEYS.statementOfProfitOrLossByNature);

    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All fields have been reset',
      life: TOAST_DURATION_SHORT_MS
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.reloadPromptDismissed()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Reload required',
        detail: 'Please refresh the page to obtain the latest version of this sheet.',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }
    if (this.statementOfProfitOrLossByNatureForm.valid) {
      const formData = this.statementOfProfitOrLossByNatureForm.value;
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Statement of Profit or Loss (By Nature) submitted:', formData);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Statement of profit or loss (by nature) data saved successfully',
        life: TOAST_DURATION_DEFAULT_MS
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please check all required fields',
        life: TOAST_DURATION_DEFAULT_MS
      });
    }
  }

  /**
   * Check if a row is currently in edit mode
   */
  isRowInEditMode(control: FieldControl): boolean {
    return this.editingRows.has(control.qname);
  }

  /**
   * Enter edit mode for a row - store original values, enable controls
   */
  enterEditMode(control: FieldControl): void {
    // Don't allow editing calculated or readonly fields
    if (this.isCalculatedField(control)) {
      return;
    }

    // Determine which suffixes to use
    const suffixes: string[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious');
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'CompanyCurrent');
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('Current', 'Previous');
    } else {
      suffixes.push('Current');
    }

    const controlNames = suffixes.map(suffix => control.qname + suffix);

    // Store original values
    const originalValues: { [key: string]: number | null } = {};
    controlNames.forEach(name => {
      originalValues[name] = this.statementOfProfitOrLossByNatureForm.get(name)?.value ?? null;
    });
    this.editingRows.set(control.qname, originalValues);
    this.stateService.markSheetEditing(SHEET_KEYS.statementOfProfitOrLossByNature);

    // Enable all controls
    controlNames.forEach(name => {
      enableAndRevealErrors(this.statementOfProfitOrLossByNatureForm.get(name), { emitEvent: true });
    });
  }

  /**
   * Save edit mode - validate, save, disable controls
   */
  saveEditMode(control: FieldControl): void {
    if (!this.editingRows.has(control.qname)) {
      return;
    }

    // Determine which suffixes to use
    const suffixes: string[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious');
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'CompanyCurrent');
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('Current', 'Previous');
    } else {
      suffixes.push('Current');
    }

    const controlNames = suffixes.map(suffix => control.qname + suffix);

    // Mark controls as pristine and disable them
    controlNames.forEach(name => {
      const formControl = this.statementOfProfitOrLossByNatureForm.get(name);
      if (formControl) {
        formControl.markAsPristine();
        formControl.disable({ emitEvent: false });
      }
    });

    // Add to edited rows set (persists until page refresh)
    this.editedRows.add(control.qname);

    // Remove from editing set
    this.editingRows.delete(control.qname);

    // Recalculate totals FIRST so the persisted subset includes the freshly
    // recalculated calculated/derived totals (downstream sheets read these).
    this.calculateTotals();

    // Subset persist: this row's controlNames PLUS the recalculated calculated totals
    const subset = pickSubset(this.statementOfProfitOrLossByNatureForm.getRawValue(), [...controlNames, ...this.calculatedTotalKeys]);
    this.stateService.updateSheetData(SHEET_KEYS.statementOfProfitOrLossByNature, subset);

    // Re-run same-sheet validations
    this.validateSameSheet();

    // Reconcile edited flag + clear the editing gate
    this.stateService.reconcileEditedFlag(SHEET_KEYS.statementOfProfitOrLossByNature);
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfProfitOrLossByNature);

    this.messageService.add({
      severity: 'success',
      summary: 'Saved',
      detail: `${control.label} updated successfully`,
      life: TOAST_DURATION_SHORT_MS
    });
  }

  /**
   * Cancel edit mode - revert to original values
   */
  cancelEditMode(control: FieldControl): void {
    const originalValues = this.editingRows.get(control.qname);
    if (!originalValues) {
      return;
    }

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const adopted = this.stateService.getSheetData(SHEET_KEYS.statementOfProfitOrLossByNature);
    Object.keys(originalValues).forEach(name => {
      const formControl = this.statementOfProfitOrLossByNatureForm.get(name);
      if (formControl) {
        const revertValue = name in adopted
          ? adopted[name]
          : (originalValues[name] ?? null);
        // { emitEvent: false } for parity with the other 9 inline sheets'
        // cancelEditMode — harmless today (this sheet's live pipeline has no
        // persist step reacting to valueChanges), but the moment anyone adds
        // one, an un-flagged setValue here would silently become an
        // adoption write-back.
        formControl.setValue(revertValue, { emitEvent: false });
        formControl.markAsPristine();
        formControl.disable();
      }
    });

    // Remove from editing set
    this.editingRows.delete(control.qname);

    // Recalculate totals and re-run same-sheet validations (no cross-sheet on cancel)
    this.calculateTotals();
    this.validateSameSheet();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfProfitOrLossByNature);
  }

  /**
   * Get validation status for a row (checks all columns)
   * Returns: 'Failed' | 'Edited' | 'Valid*' | 'Valid' | ''
   */
  getRowValidationStatus(control: FieldControl): 'Failed' | 'Edited' | 'Valid*' | 'Valid' | '' {
    // Only check monetary items
    if (control.ui.control !== 'number') return '';

    // Determine which suffixes to use
    const suffixes: string[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious');
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'CompanyCurrent');
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('Current', 'Previous');
    } else {
      suffixes.push('Current');
    }

    let hasInvalid = false;
    let hasEdited = false;
    let hasValue = false;

    for (const suffix of suffixes) {
      const controlName = control.qname + suffix;
      const formControl = this.statementOfProfitOrLossByNatureForm.get(controlName);

      if (formControl) {
        if (formControl.invalid && (formControl.dirty || formControl.touched)) hasInvalid = true;
        if (!formControl.pristine) hasEdited = true;
        if (formControl.value !== null && formControl.value !== undefined && formControl.value !== '') hasValue = true;
      }
    }

    if (hasInvalid) return 'Failed';
    // Check if row has been saved as edited (persists until page refresh)
    if (this.editedRows.has(control.qname)) return 'Edited';
    if (hasEdited) return 'Edited';

    // Check if there are validation errors (ExtractedSum vs CalculatedSum mismatch)
    if (hasValue && this.validationErrors.has(control.qname)) {
      return 'Valid*';
    }

    if (hasValue) return 'Valid';
    return '';
  }

  /**
   * Get error description for a row (aggregates from all columns)
   */
  getRowErrorDescription(control: FieldControl): string {
    // Only check monetary items
    if (control.ui.control !== 'number') return '';

    // Check validation errors first (from validateAgainstApiData)
    if (this.validationErrors.has(control.qname)) {
      const validationErrors = this.validationErrors.get(control.qname) || [];
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`📝 getRowErrorDescription for ${control.qname}:`, validationErrors);
      // Format as numbered list
      return this.appendBaselineDebug(
        control,
        validationErrors
          .map((error, index) => `${index + 1}. ${error}`)
          .join('\n\n')
      );
    }

    // Fall back to regular form validation errors
    const suffixes: string[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious');
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'CompanyCurrent');
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('Current', 'Previous');
    } else {
      suffixes.push('Current');
    }

    const columnLabels = suffixes.map(suffix => {
      return suffix.replace('Group', 'Group ').replace('Company', 'Company ').replace('Current', 'Current').replace('Previous', 'Previous');
    });

    const errors: string[] = [];

    suffixes.forEach((suffix, idx) => {
      const controlName = control.qname + suffix;
      const formControl = this.statementOfProfitOrLossByNatureForm.get(controlName);

      if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
        const errorMessages: string[] = [];

        if (formControl.errors) {
          if (formControl.errors['required']) errorMessages.push('Required field');
          if (formControl.errors['min']) errorMessages.push(`Min: ${formControl.errors['min'].min}`);
          if (formControl.errors['max']) errorMessages.push(`Max: ${formControl.errors['max'].max}`);
        }

        if (errorMessages.length > 0) {
          errors.push(`${columnLabels[idx]}: ${errorMessages.join(', ')}`);
        }
      }
    });

    return this.appendBaselineDebug(control, errors.join('. '));
  }

  /**
   * Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
   * real validation text this row already has (often none for a clean total).
   * baselineDebugForControl returns '' unless this row is a qualifying total.
   * Columns + getControlName rebuilt inline to mirror validateAgainstApiData's
   * computeMismatchWarnings call exactly (same visible suffixes + concat keys).
   */
  private appendBaselineDebug(control: FieldControl, current: string): string {
    if (!this.baselineDebug.enabled()) return current;
    const columns: { key: string; label: string }[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      columns.push(
        { key: 'GroupCurrent', label: 'Group Current' },
        { key: 'GroupPrevious', label: 'Group Previous' },
        { key: 'CompanyCurrent', label: 'Company Current' },
        { key: 'CompanyPrevious', label: 'Company Previous' },
      );
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      columns.push(
        { key: 'GroupCurrent', label: 'Group Current' },
        { key: 'CompanyCurrent', label: 'Company Current' },
      );
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      columns.push(
        { key: 'Current',  label: 'Current' },
        { key: 'Previous', label: 'Previous' },
      );
    } else {
      columns.push({ key: 'Current', label: 'Current' });
    }
    const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
      SHEET_KEYS.statementOfProfitOrLossByNature,
      control as unknown as SummableControl,
      columns,
      (qname, key) => qname + key
    );
    if (!baselineInfo) return current;
    return current ? `${current}${baselineInfo}` : baselineInfo;
  }

  /**
   * Check if row has failed validation
   */
  hasRowFailed(control: FieldControl): boolean {
    return this.getRowValidationStatus(control) === 'Failed';
  }

  /**
   * Check if row has validation error (for yellow highlighting)
   * Checks validationErrors Map directly instead of status
   */
  hasRowValidationError(control: FieldControl): boolean {
    return this.validationErrors.has(control.qname);
  }
  /**
   * Does this field's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet answers the
   * question the same way as every other.
   *
   * **Wired even though this sheet declares ZERO mandatory controls today.**
   * That is deliberate, and it is the lesson from roles 9, 13 and 15: they
   * were skipped during the 2026-08-11 marker rollout for having no mandatory
   * fields, and when the PO's enumerated totals list flagged their calculated
   * totals hours later the flags landed with nowhere to render — the `*`
   * silently did not appear until smoke testing caught it. A sheet that
   * renders schema-driven labels gets the marker regardless of its current
   * mandatory count, so the next list cannot reopen that gap. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves.
   * Same-sheet `selfData` is passed only by sheets keyed by bare qname; the
   * per-cell sheets (`qname[Dimension][Period]`) deliberately omit it, since a
   * same-sheet lookup would silently miss.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FieldControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
